import { LensCustomization, StockSettings } from '@ca/common'
import {
  BundleType,
  OrderItem,
  OrderResultActions,
  StepsState,
} from '@order/orderTypes'
import { debugOrder } from '@order/utils/getDebugLog'
import { nanoid } from 'nanoid/non-secure'
import { StateCreator } from 'zustand'
import { OrderState } from './orderState'
import { StockSettingsKey, StockUtils } from '@frame/StockUtils'
import Analytics from 'hooks/analytics/Analytics'
import { partition } from 'lodash'
import { useRemoteConfigStore } from 'hooks/useRemoteConfigStore'

export type OrderItemsActions = {
  setItems: (item: OrderItem[]) => void
  removeItem: (itemId: string) => void
  switchActiveItem: (itemId: string) => void
  /**
   * Adds selected frame as new item to the order and sets it as active
   */
  setFrame: (parts: {
    frame: OrderItem['frame']
    bundleType?: BundleType
    flowName?: string
  }) => void
  clearFrame: (itemId: string) => void

  setTemple: (itemId: string, temple: OrderItem['temple']) => void

  /**
   * Adds a new Partial<OrderItem> to the order
   */
  addItem: (parts?: Partial<OrderItem>) => OrderItem

  setStock: (stockSettingsKey: StockSettingsKey, source: StockSettings) => void
  resetStock: (stockSettingsKey: StockSettingsKey) => void
  setDefaultStock: (item: OrderItem) => void
  removeByBundle: (bundleType: BundleType) => void
  resetItemCompleted: () => void
  setCustomization: (
    itemId: string,
    customization: LensCustomization | undefined
  ) => void
  setItemFlowName: (itemId: string, flowName: string | undefined) => void
}

export const orderItemsSlice: StateCreator<
  OrderItemsActions & { order: OrderState } & OrderResultActions,
  [['zustand/immer', never], never],
  [],
  OrderItemsActions
> = (set, get) => ({
  removeByBundle: (bundleType: BundleType) =>
    set((state) => {
      const [items, removed] = partition(
        state.order.items,
        (item) => item.bundleType !== bundleType
      )

      removed.forEach((item) => {
        Analytics.logEvent('frame_removed', {
          bundle: item.bundleType,
          frame: item.frame?.default_code || '',
        })
      })
      state.order.items = items
    }),
  switchActiveItem: (itemId) =>
    set((state) => {
      state.order.activeItemId = itemId
    }),

  setFrame: ({ frame, bundleType = 'base', flowName }) => {
    debugOrder.log('setFrame', frame, bundleType)
    Analytics.logEvent('frame_selected', {
      bundle: bundleType,
      frame: frame?.default_code || '',
    })
    const { addItem, order, setDefaultStock, invalidateResult } = get()
    const { activeItemId: currentItemId } = order
    const currentItem =
      currentItemId &&
      order.items.find(
        (item) => item.id === currentItemId && item.bundleType === bundleType
      )
    debugOrder.log('order state setFrame', { currentItem, bundleType })
    if (!currentItem) {
      const item = addItem({ bundleType, frame, flowName })
      set((state) => {
        state.order.activeItemId = item.id
      })
      return
    }
    set((state) => {
      const item =
        currentItemId &&
        state.order.items.find((item) => item.id === currentItemId)
      if (!item) {
        return
      }
      item.frame = frame
      item.flowName = flowName
      item.temple = undefined
      item.isCompleted = false
      delete state.order.stock[StockUtils.getItemKey(item.id, 'frame')]
      delete state.order.stock[StockUtils.getItemKey(item.id, 'temple')]
      setDefaultStock(item)
    })
    invalidateResult()
  },
  clearFrame: (itemId) => {
    const { invalidateResult } = get()
    set((state) => {
      const item = state.order.items.find((item) => item.id === itemId)
      if (!item) {
        return
      }

      item.frame = undefined
      item.temple = undefined
      item.isCompleted = false
      delete state.order.stock[StockUtils.getItemKey(itemId, 'frame')]
      delete state.order.stock[StockUtils.getItemKey(itemId, 'temple')]
    })
    invalidateResult()
  },
  addItem: (parts?: Partial<OrderItem>) => {
    debugOrder.log('addItem', parts)
    if (parts?.frame) {
      Analytics.logEvent('frame_selected', {
        bundle: parts.bundleType ?? 'base',
        frame: parts.frame.default_code,
      })
    }
    const item: OrderItem = {
      bundleType: 'base',
      ...parts,
      id: nanoid(),
      isCompleted: false,
    }
    const { setDefaultStock, invalidateResult } = get()
    set((state) => {
      state.order.items = [...state.order.items, item]
      // Create bundle config if it doesn't exist
      const { bundleType } = item
      if (!state.order.bundles[bundleType]) {
        state.order.bundles[bundleType] = {
          type: bundleType,
          steps: [],
        }
      }
    })
    invalidateResult()
    setDefaultStock(item)

    return item
  },
  setItems: (items) =>
    set((state) => {
      state.order.items = items
    }),

  setTemple: (itemId, temple) =>
    set((state) => {
      const item = state.order.items.find((item) => item.id === itemId)
      if (!item) {
        return
      }
      item.temple = temple
    }),

  setStock: (orderItemId, source) =>
    set((state) => {
      state.order.stock[orderItemId] = source
    }),
  resetStock: (orderItemId) =>
    set((state) => {
      const copyStock = { ...state.order.stock }
      delete copyStock[orderItemId]
      state.order.stock = copyStock
    }),

  setDefaultStock: (item: OrderItem) =>
    set((state) => {
      const config = useRemoteConfigStore.getState().config
      const frameStock = StockUtils.getDefaultStock(
        item,
        config?.region,
        'frame'
      )
      const templeStock = StockUtils.getDefaultStock(
        item,
        config?.region,
        'temple'
      )
      if (frameStock) {
        state.order.stock[StockUtils.getItemKey(item.id, 'frame')] = frameStock
      }
      if (templeStock) {
        state.order.stock[StockUtils.getItemKey(item.id, 'temple')] =
          templeStock
      }
    }),
  setCustomization: (itemId, customization) =>
    set((state) => {
      const item = state.order.items.find((item) => item.id === itemId)
      if (!item) {
        return
      }
      item.customization = customization
    }),

  setItemFlowName: (itemId, flowName) =>
    set((state) => {
      const item = state.order.items.find((item) => item.id === itemId)
      if (!item) {
        return
      }
      item.flowName = flowName
    }),

  resetItemCompleted: () =>
    set((state) => {
      const itemId = state.order.activeItemId
      if (!itemId) {
        return
      }
      const item = state.order.items.find((item) => item.id === itemId)
      if (!item) {
        return
      }
      item.isCompleted = false
    }),

  removeItem: (itemId) =>
    set((state) => {
      if (state.order.activeItemId === itemId) {
        const next = state.order.items.find((item) => item.id !== itemId)
        state.order.activeItemId = next?.id
      }
      const item = state.order.items.find((item) => item.id === itemId)
      if (item) {
        Analytics.logEvent('frame_removed', {
          bundle: item.bundleType,
          frame: item.frame?.default_code || '',
        })
      }
      state.order.items = state.order.items.filter((item) => item.id !== itemId)
      // Remove both frame and temple stock
      delete state.order.stock[StockUtils.getItemKey(itemId, 'frame')]
      delete state.order.stock[StockUtils.getItemKey(itemId, 'temple')]
    }),
})
